import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { printHtmlSilently } from '../lib/silentPrint';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import axios from 'axios';
import {
  DollarSign, TrendingUp, TrendingDown, Calendar as CalendarIcon, XCircle, BarChart3, FileBarChart, UserCircle, Wallet, Printer, ClipboardList, Trash2, Package
} from 'lucide-react';
import { CashSessionContent } from './CashSessionPage';
import { ShiftSummaryContent } from './ShiftSummaryPage';
import { PDVReceivablesModal } from '../components/PDVReceivablesModal';
import { ReceivablesReport } from '../components/ReceivablesReport';
import { Tabs as InnerTabs, TabsContent as InnerTabsContent, TabsList as InnerTabsList, TabsTrigger as InnerTabsTrigger } from '../components/ui/tabs';
import { DateRangeFilter, downloadCsv } from '../components/DateRangeFilter';

import { CategoryProfitModal } from '../components/CategoryProfitModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_COLORS = { acougue: '#dc2626', bebidas: '#2563eb', mercearia: '#facc15', flv: '#16a34a', padaria: '#d97706', frios_laticinios: '#0891b2', limpeza: '#7c3aed', higiene: '#db2777', outros: '#64748b' };
const CATEGORY_LABELS = { acougue: 'Açougue', bebidas: 'Bebidas', mercearia: 'Mercearia', flv: 'FLV', padaria: 'Padaria', frios_laticinios: 'Frios e Laticínios', limpeza: 'Limpeza', higiene: 'Higiene', outros: 'Outros' };

const MONTHS_PT = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

export const CashflowPage = () => {
  const [tab, setTab] = useState('movements');
  const [showCrediarioModal, setShowCrediarioModal] = useState(false);
  const [entries, setEntries] = useState([]);
  const [balance, setBalance] = useState({ total_entradas: 0, total_saidas: 0, saldo: 0 });
  const [daily, setDaily] = useState(null);
  const [cancelledSales, setCancelledSales] = useState([]);
  const [inventoryEntries, setInventoryEntries] = useState([]);
  const [inventorySales, setInventorySales] = useState(0);
  const [profitToday, setProfitToday] = useState(null);
  const [profitCat, setProfitCat] = useState(null);
  const [report, setReport] = useState(null);
  const todayStr = new Date().toISOString().substring(0, 10);
  const firstDayStr = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().substring(0, 10);
  const [reportStart, setReportStart] = useState(firstDayStr);
  const [reportEnd, setReportEnd] = useState(todayStr);
  const [showForm, setShowForm] = useState(false);
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [formData, setFormData] = useState({
    type: 'entrada', category: 'outros', amount: '',
    description: '', payment_method: 'dinheiro', notes: ''
  });
  const [loading, setLoading] = useState(false);
  // Filtros de período por aba
  const [movRange, setMovRange] = useState({ start: todayStr, end: todayStr });
  const [cancelRange, setCancelRange] = useState({ start: todayStr, end: todayStr });

  useEffect(() => { loadAll(); }, [movRange]);
  useEffect(() => {
    if (tab === 'daily') loadDaily();
    if (tab === 'cancelled') loadCancelled();
    if (tab === 'report') loadReport();
    if (tab === 'inventory') loadInventory();
  }, [tab, year, month, cancelRange, movRange]);

  const loadInventory = async () => {
    try {
      const params = `?start_date=${movRange.start}&end_date=${movRange.end}&inventory_only=true&limit=500`;
      const [eRes, sRes] = await Promise.all([
        axios.get(`${API}/cashflow${params}`, { withCredentials: true }),
        axios.get(`${API}/cashflow/sales-report?start_date=${movRange.start}&end_date=${movRange.end}`, { withCredentials: true }).catch(() => ({ data: { total_liquido: 0 } })),
      ]);
      setInventoryEntries(eRes.data || []);
      setInventorySales(Number(sRes.data?.total_liquido || 0));
    } catch (err) { console.error(err); }
  };

  const loadAll = async () => {
    try {
      const params = `?start_date=${movRange.start}&end_date=${movRange.end}`;
      const [eRes, bRes, pRes] = await Promise.all([
        axios.get(`${API}/cashflow${params}`, { withCredentials: true }),
        axios.get(`${API}/cashflow/balance${params}`, { withCredentials: true }),
        axios.get(`${API}/dashboard/profit-today${params}`, { withCredentials: true })
      ]);
      setEntries(eRes.data);
      setBalance(bRes.data);
      setProfitToday(pRes.data);
    } catch (err) { console.error(err); }
  };

  const loadDaily = async () => {
    try {
      const { data } = await axios.get(`${API}/cashflow/daily?year=${year}&month=${month}`, { withCredentials: true });
      setDaily(data);
    } catch (err) { console.error(err); }
  };

  const loadCancelled = async () => {
    try {
      const { data } = await axios.get(`${API}/sales/cancelled?start_date=${cancelRange.start}&end_date=${cancelRange.end}`, { withCredentials: true });
      setCancelledSales(data);
    } catch (err) { console.error(err); }
  };

  const loadReport = async () => {
    try {
      const { data } = await axios.get(`${API}/cashflow/sales-report?start_date=${reportStart}&end_date=${reportEnd}`, { withCredentials: true });
      setReport(data);
    } catch (err) { console.error(err); }
  };

  const fmtBr = (d) => {
    if (!d) return '';
    const [y, m, day] = String(d).split('-');
    return `${day}/${m}/${y}`;
  };

  const handlePrintReport = () => {
    if (!report) return;
    const methodLabel = (m) => ({
      dinheiro: 'Dinheiro', cartao: 'Cartao', cartao_credito: 'Cartao Cred.',
      cartao_debito: 'Cartao Deb.', pix: 'PIX Loja', pix_maquininha: 'PIX Maq.',
      boleto: 'Boleto', a_prazo: 'A Prazo', crediario: 'Crediario'
    }[m] || (m || '').replace('_', ' '));

    const paymentRows = (report.by_payment_method || []).map(p =>
      `<tr><td>${methodLabel(p.method)}</td><td class="right">${p.count}</td><td class="right">R$ ${Number(p.total).toFixed(2)}</td></tr>`
    ).join('') || `<tr><td colspan="3" class="center small">Sem dados</td></tr>`;

    const categoryRows = (report.by_category || []).map(c =>
      `<tr><td>${(CATEGORY_LABELS[c.category] || c.category)}</td><td class="right">${Number(c.quantity).toFixed(2).replace(/\.00$/, '')}</td><td class="right">R$ ${Number(c.total).toFixed(2)}</td></tr>`
    ).join('') || `<tr><td colspan="3" class="center small">Sem dados</td></tr>`;

    const html = `
      <div class="center bold" style="font-size:13px">RELATORIO DE VENDAS</div>
      <div class="center small">${fmtBr(reportStart)} ate ${fmtBr(reportEnd)}</div>
      <div class="center small">Emitido: ${new Date().toLocaleString('pt-BR')}</div>
      <hr/>
      <table><tbody>
        <tr><td>N. Vendas:</td><td class="right bold">${report.sales_count}</td></tr>
        <tr><td>Total Bruto:</td><td class="right">R$ ${Number(report.total_bruto).toFixed(2)}</td></tr>
        <tr><td>Descontos:</td><td class="right">- R$ ${Number(report.total_desconto).toFixed(2)}</td></tr>
        <tr><td class="bold">Total Liquido:</td><td class="right bold">R$ ${Number(report.total_liquido).toFixed(2)}</td></tr>
        <tr><td>Ticket Medio:</td><td class="right">R$ ${Number(report.ticket_medio).toFixed(2)}</td></tr>
      </tbody></table>
      <hr/>
      <div class="bold" style="margin-bottom:2px">FORMAS DE PAGAMENTO</div>
      <table><thead>
        <tr><th class="left small">Forma</th><th class="right small">Qtd</th><th class="right small">Valor</th></tr>
      </thead><tbody>${paymentRows}</tbody></table>
      <hr/>
      <div class="bold" style="margin-bottom:2px">VENDAS POR CATEGORIA</div>
      <table><thead>
        <tr><th class="left small">Categoria</th><th class="right small">Qtd</th><th class="right small">Valor</th></tr>
      </thead><tbody>${categoryRows}</tbody></table>
      <hr/>
      <div class="center small">Smart Flow</div>
    `;

    printHtmlSilently(`
      <html><head><title>Relatorio de Vendas</title>
      <style>
        @page { margin: 0; size: 80mm auto; }
        * { box-sizing: border-box; }
        body { font-family: 'Courier New', monospace; font-size: 11px; padding: 8px; color: #000; margin: 0; }
        hr { border: none; border-top: 1px dashed #000; margin: 6px 0; }
        table { width: 100%; border-collapse: collapse; }
        td, th { padding: 2px 0; vertical-align: top; }
        .right { text-align: right; }
        .center { text-align: center; }
        .left { text-align: left; }
        .bold { font-weight: bold; }
        .small { font-size: 10px; }
      </style>
      </head><body>${html}</body></html>
    `);
  };

  const handlePrintDaily = () => {
    if (!daily) return;
    const dayRows = (daily.days || []).filter(d => d.sales_count > 0 || d.cash_out > 0).map(d =>
      `<tr><td>${String(d.day).padStart(2,'0')}/${String(month).padStart(2,'0')}</td><td class="right">${d.sales_count}</td><td class="right">R$ ${Number(d.sales_gross).toFixed(2)}</td><td class="right">R$ ${Number(d.profit).toFixed(2)}</td></tr>`
    ).join('') || `<tr><td colspan="4" class="center small">Sem movimentos</td></tr>`;

    const html = `
      <div class="center bold" style="font-size:13px">MOVIMENTACAO DIARIA</div>
      <div class="center small">${MONTHS_PT[month-1]} / ${year}</div>
      <div class="center small">Emitido: ${new Date().toLocaleString('pt-BR')}</div>
      <hr/>
      <table><thead>
        <tr><th class="left small">Dia</th><th class="right small">Qtd</th><th class="right small">Vendas</th><th class="right small">Lucro</th></tr>
      </thead><tbody>${dayRows}</tbody></table>
      <hr/>
      <table><tbody>
        <tr><td>Total Vendas:</td><td class="right">${daily.totals.sales_count}</td></tr>
        <tr><td>Bruto:</td><td class="right">R$ ${Number(daily.totals.sales_gross).toFixed(2)}</td></tr>
        <tr><td>Custo:</td><td class="right">R$ ${Number(daily.totals.cost_of_goods).toFixed(2)}</td></tr>
        <tr><td class="bold">Lucro:</td><td class="right bold">R$ ${Number(daily.totals.profit).toFixed(2)}</td></tr>
        <tr><td>Saidas:</td><td class="right">R$ ${Number(daily.totals.cash_out).toFixed(2)}</td></tr>
      </tbody></table>
      <hr/>
      <div class="center small">Smart Flow</div>
    `;
    printHtmlSilently(`
      <html><head><title>Relatorio Diario</title>
      <style>
        @page { margin: 0; size: 80mm auto; }
        body { font-family: 'Courier New', monospace; font-size: 11px; padding: 8px; color: #000; margin: 0; }
        hr { border: none; border-top: 1px dashed #000; margin: 6px 0; }
        table { width: 100%; border-collapse: collapse; }
        td, th { padding: 2px 0; }
        .right { text-align: right; } .center { text-align: center; } .left { text-align: left; }
        .bold { font-weight: bold; } .small { font-size: 10px; }
      </style>
      </head><body>${html}</body></html>
    `);
  };

  const handlePrintMovements = () => {
    const rows = (entries || []).slice(0, 80).map(e =>
      `<tr><td class="small">${new Date(e.created_at).toLocaleDateString('pt-BR')}</td><td class="small">${(e.description || '').substring(0,18)}</td><td class="right small">${e.type === 'entrada' ? '+' : '-'}R$ ${Number(e.amount).toFixed(2)}</td></tr>`
    ).join('') || `<tr><td colspan="3" class="center small">Sem movimentos</td></tr>`;
    const html = `
      <div class="center bold" style="font-size:13px">MOVIMENTACOES</div>
      <div class="center small">Emitido: ${new Date().toLocaleString('pt-BR')}</div>
      <hr/>
      <table><thead>
        <tr><th class="left small">Data</th><th class="left small">Descricao</th><th class="right small">Valor</th></tr>
      </thead><tbody>${rows}</tbody></table>
      <hr/>
      <table><tbody>
        <tr><td>Entradas:</td><td class="right">R$ ${Number(balance.total_entradas).toFixed(2)}</td></tr>
        <tr><td>Saidas:</td><td class="right">R$ ${Number(balance.total_saidas).toFixed(2)}</td></tr>
        <tr><td class="bold">Saldo:</td><td class="right bold">R$ ${Number(balance.saldo).toFixed(2)}</td></tr>
      </tbody></table>
      <hr/>
      <div class="center small">Smart Flow</div>
    `;
    printHtmlSilently(`
      <html><head><title>Movimentacoes</title>
      <style>
        @page { margin: 0; size: 80mm auto; }
        body { font-family: 'Courier New', monospace; font-size: 11px; padding: 8px; color: #000; margin: 0; }
        hr { border: none; border-top: 1px dashed #000; margin: 6px 0; }
        table { width: 100%; border-collapse: collapse; }
        td, th { padding: 2px 0; }
        .right { text-align: right; } .center { text-align: center; } .left { text-align: left; }
        .bold { font-weight: bold; } .small { font-size: 10px; }
      </style>
      </head><body>${html}</body></html>
    `);
  };

  const handleDeleteEntry = async (entry) => {
    const reason = window.prompt(
      `Excluir movimentação?\n\n${entry.description}\nValor: ${entry.type === 'entrada' ? '+' : '-'} R$ ${Number(entry.amount).toFixed(2)}\n\nDigite o motivo (mín. 3 caracteres):`
    );
    if (reason === null) return;
    if (!reason || reason.trim().length < 3) {
      alert('Motivo obrigatório (mín. 3 caracteres).');
      return;
    }
    try {
      await axios.delete(`${API}/cashflow/${entry.id}`, {
        params: { reason: reason.trim() },
        withCredentials: true,
      });
      await loadAll();
    } catch (err) {
      alert(err?.response?.data?.detail || 'Erro ao excluir movimentação.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/cashflow`, {
        ...formData, amount: parseFloat(formData.amount)
      }, { withCredentials: true });
      loadAll();
      setFormData({ type: 'entrada', category: 'outros', amount: '', description: '', payment_method: 'dinheiro', notes: '' });
      setShowForm(false);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const years = [now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1];

  return (
    <Layout>
      <div className="p-8" data-testid="cashflow-page">
        <h1 className="text-4xl font-black text-slate-900 tracking-tight mb-6">Fluxo de Caixa</h1>

        {/* Cards menores em uma linha */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          <div className="bg-white rounded-md border border-slate-200 p-3" data-testid="kpi-entradas">
            <div className="flex items-center justify-between">
              <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Entradas</p>
              <TrendingUp size={16} className="text-green-600" />
            </div>
            <p className="text-xl font-bold text-green-600">R$ {balance.total_entradas.toFixed(2)}</p>
          </div>
          <div className="bg-white rounded-md border border-slate-200 p-3" data-testid="kpi-saidas">
            <div className="flex items-center justify-between">
              <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Saídas</p>
              <TrendingDown size={16} className="text-red-600" />
            </div>
            <p className="text-xl font-bold text-red-600">R$ {balance.total_saidas.toFixed(2)}</p>
          </div>
          <div className="bg-white rounded-md border border-slate-200 p-3" data-testid="kpi-saldo">
            <div className="flex items-center justify-between">
              <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Saldo</p>
              <DollarSign size={16} className="text-blue-600" />
            </div>
            <p className="text-xl font-bold text-blue-600">R$ {balance.saldo.toFixed(2)}</p>
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid w-full grid-cols-2 sm:grid-cols-4 mb-6 h-auto gap-1">
            <TabsTrigger value="movements" data-testid="tab-movements" className="py-2">
              <BarChart3 size={16} className="mr-2" /> Movimentações
            </TabsTrigger>
            <TabsTrigger value="inventory" data-testid="tab-inventory" className="py-2">
              <Package size={16} className="mr-2" /> Estoque
            </TabsTrigger>
            <TabsTrigger value="daily" data-testid="tab-daily" className="py-2">
              <CalendarIcon size={16} className="mr-2" /> Por Dia
            </TabsTrigger>
            <TabsTrigger value="report" data-testid="tab-report" className="py-2">
              <FileBarChart size={16} className="mr-2" /> Relatórios
            </TabsTrigger>
            <TabsTrigger value="cancelled" data-testid="tab-cancelled" className="py-2">
              <XCircle size={16} className="mr-2" /> Canceladas
            </TabsTrigger>
            <TabsTrigger value="crediario" data-testid="tab-crediario" className="py-2">
              <Wallet size={16} className="mr-2" /> Crediário
            </TabsTrigger>
            <TabsTrigger value="cash-session" data-testid="tab-cash-session" className="py-2">
              <UserCircle size={16} className="mr-2" /> Abrir/Fechar Caixa
            </TabsTrigger>
            <TabsTrigger value="shifts" data-testid="tab-shifts" className="py-2">
              <ClipboardList size={16} className="mr-2" /> Resumo de Turnos
            </TabsTrigger>
          </TabsList>

          <TabsContent value="movements">
            <DateRangeFilter
              value={movRange}
              onChange={setMovRange}
              onExportCsv={() => {
                downloadCsv(`movimentacoes_${movRange.start}_a_${movRange.end}.csv`, entries, [
                  { key: 'created_at', label: 'Data', format: (v) => v ? new Date(v).toLocaleString('pt-BR') : '' },
                  { key: 'type', label: 'Tipo', format: (v) => v === 'entrada' ? 'Entrada' : 'Saída' },
                  { key: 'category', label: 'Categoria' },
                  { key: 'description', label: 'Descrição' },
                  { key: 'payment_method', label: 'Forma' },
                  { key: 'amount', label: 'Valor (R$)', format: (v) => Number(v || 0).toFixed(2).replace('.', ',') },
                  { key: 'user_name', label: 'Operador' },
                  { key: 'notes', label: 'Observações' },
                ]);
              }}
              exportLabel="Exportar CSV"
              className="mb-4"
            />
            {profitToday && (
              <div className="bg-white rounded-md border border-slate-200 p-6 mb-6" data-testid="profit-today-card">
                <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                  <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
                    <TrendingUp size={22} className="text-green-600" /> {movRange.start === movRange.end ? 'Lucro do Dia por Categoria' : 'Lucro do Período por Categoria'}
                  </h2>
                  <div className="flex gap-4 items-center flex-wrap">
                    <div className="text-right">
                      <p className="text-xs uppercase tracking-wider text-slate-500">Receita</p>
                      <p className="text-lg font-bold text-blue-600">R$ {profitToday.total_revenue.toFixed(2)}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs uppercase tracking-wider text-slate-500">Custo</p>
                      <p className="text-lg font-bold text-yellow-700">R$ {profitToday.total_cost.toFixed(2)}</p>
                    </div>
                    <div className="text-right border-l border-slate-300 pl-4">
                      <p className="text-xs uppercase tracking-wider text-green-700">Lucro</p>
                      <p className="text-2xl font-black text-green-600">R$ {profitToday.total_profit.toFixed(2)}</p>
                      <p className="text-xs text-green-700">Margem: {profitToday.total_margin_percent}%</p>
                    </div>
                  </div>
                </div>
                {profitToday.categories.length === 0 ? (
                  <p className="text-slate-400 text-center py-4 text-sm">Nenhuma venda registrada no período.</p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                    {profitToday.categories.map((c) => {
                      const positive = c.profit >= 0;
                      const catColor = CATEGORY_COLORS[c.category] || '#64748b';
                      return (
                        <div
                          key={c.category}
                          role="button"
                          tabIndex={0}
                          onClick={() => setProfitCat(c.category)}
                          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setProfitCat(c.category); } }}
                          title="Clique para ver os produtos vendidos desta categoria"
                          className={`rounded-md border-2 p-4 relative overflow-hidden cursor-pointer transition-transform hover:-translate-y-0.5 hover:shadow-md ${positive ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}`}
                          style={{ borderLeftColor: catColor, borderLeftWidth: '6px' }}
                          data-testid={`profit-cat-${c.category}`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-black text-base" style={{ color: catColor }}>{CATEGORY_LABELS[c.category] || c.category}</span>
                            <span className="text-xs uppercase tracking-wider px-2 py-0.5 rounded text-white font-bold" style={{ background: catColor }}>
                              {c.items_count} itens
                            </span>
                          </div>
                          <p className="text-xs text-slate-600">Receita: R$ {c.revenue.toFixed(2)}</p>
                          <p className="text-xs text-slate-600">Custo: R$ {c.cost.toFixed(2)}</p>
                          <p className={`text-2xl font-black mt-2 ${positive ? 'text-green-700' : 'text-red-700'}`}>R$ {c.profit.toFixed(2)}</p>
                          <p className={`text-xs font-bold ${positive ? 'text-green-700' : 'text-red-700'}`}>Margem: {c.margin_percent}%</p>
                          <p className="text-[10px] uppercase tracking-wider text-slate-400 mt-1">Clique para detalhar →</p>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-end mb-4 gap-2">
              <Button onClick={handlePrintMovements} variant="outline" className="border-blue-500 text-blue-700 hover:bg-blue-50" data-testid="movements-print">
                <Printer size={16} className="mr-2" /> Imprimir
              </Button>
              <Button onClick={() => setShowForm(!showForm)} className="bg-blue-600 hover:bg-blue-700">
                <DollarSign size={20} className="mr-2" /> Nova Entrada
              </Button>
            </div>

            {showForm && (
              <div className="bg-white rounded-md border border-slate-200 p-6 mb-8">
                <h2 className="text-2xl font-bold text-slate-900 mb-4">Nova Entrada/Saída</h2>
                <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Tipo *</Label>
                    <Select value={formData.type} onValueChange={(value) => setFormData({ ...formData, type: value })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="entrada">Entrada</SelectItem>
                        <SelectItem value="saida">Saída</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Categoria *</Label>
                    <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="venda">Venda</SelectItem>
                        <SelectItem value="compra">Compra</SelectItem>
                        <SelectItem value="despesa">Despesa</SelectItem>
                        <SelectItem value="outros">Outros</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Valor (R$) *</Label>
                    <Input type="number" step="0.01" value={formData.amount} onChange={(e) => setFormData({ ...formData, amount: e.target.value })} required />
                  </div>
                  <div className="space-y-2">
                    <Label>Forma de Pagamento</Label>
                    <Select value={formData.payment_method} onValueChange={(value) => setFormData({ ...formData, payment_method: value })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="dinheiro">Dinheiro</SelectItem>
                        <SelectItem value="cartao">Cartão</SelectItem>
                        <SelectItem value="pix">PIX Loja</SelectItem>
                        <SelectItem value="pix_maquininha">PIX na Maquininha</SelectItem>
                        <SelectItem value="boleto">Boleto</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label>Descrição *</Label>
                    <Input value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} required />
                  </div>
                  <div className="md:col-span-2 flex gap-2">
                    <Button type="submit" disabled={loading} className="bg-blue-600 hover:bg-blue-700">
                      {loading ? 'Salvando...' : 'Salvar'}
                    </Button>
                    <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancelar</Button>
                  </div>
                </form>
              </div>
            )}

            <div className="bg-white rounded-md border border-slate-200 overflow-hidden">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Data</th>
                    <th className="px-6 py-3 text-left text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Tipo</th>
                    <th className="px-6 py-3 text-left text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Categoria</th>
                    <th className="px-6 py-3 text-left text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Descrição</th>
                    <th className="px-6 py-3 text-left text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Valor</th>
                    <th className="px-6 py-3 text-left text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Usuário</th>
                    <th className="px-6 py-3 text-right text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {entries.map((entry) => (
                    <tr key={entry.id} className="hover:bg-slate-50">
                      <td className="px-6 py-4 text-sm text-slate-600">{new Date(entry.created_at).toLocaleString('pt-BR')}</td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          {entry.type === 'entrada' ? <TrendingUp size={18} className="text-green-600" /> : <TrendingDown size={18} className="text-red-600" />}
                          <span className="capitalize">{entry.type}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4"><span className="px-2 py-1 rounded-md text-xs font-medium bg-slate-100 text-slate-600 capitalize">{entry.category}</span></td>
                      <td className="px-6 py-4 text-sm text-slate-900">{entry.description}</td>
                      <td className="px-6 py-4"><span className={`font-bold ${entry.type === 'entrada' ? 'text-green-600' : 'text-red-600'}`}>{entry.type === 'entrada' ? '+' : '-'} R$ {entry.amount.toFixed(2)}</span></td>
                      <td className="px-6 py-4 text-sm text-slate-600">{entry.user_name}</td>
                      <td className="px-6 py-4 text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteEntry(entry)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          data-testid={`cashflow-delete-${entry.id}`}
                          title="Excluir movimentação"
                        >
                          <Trash2 size={16} />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="inventory">
            <DateRangeFilter
              value={movRange}
              onChange={setMovRange}
              onExportCsv={() => {
                downloadCsv(`entradas_mercadoria_${movRange.start}_a_${movRange.end}.csv`, inventoryEntries, [
                  { key: 'created_at', label: 'Data', format: (v) => new Date(v).toLocaleString('pt-BR') },
                  { key: 'type', label: 'Tipo' },
                  { key: 'category', label: 'Categoria' },
                  { key: 'description', label: 'Descrição' },
                  { key: 'amount', label: 'Valor (R$)', format: (v) => Number(v).toFixed(2) },
                  { key: 'user_name', label: 'Usuário' },
                ]);
              }}
            />
            <div className="bg-emerald-50 border-l-4 border-emerald-500 rounded-md p-3 mb-4 text-sm text-emerald-900">
              <strong>Entradas de Mercadoria</strong> (Notas Fiscais, Fracionamento de Carne, Estornos).
              Estes lançamentos NÃO entram no saldo do caixa do dia. São contabilizados apenas para controle de custo de estoque.
            </div>
            {(() => {
              const totalCompras = inventoryEntries.filter(e => e.type === 'saida').reduce((s, e) => s + Number(e.amount || 0), 0);
              const totalEstornos = inventoryEntries.filter(e => e.type === 'entrada').reduce((s, e) => s + Number(e.amount || 0), 0);
              const comprasLiquidas = totalCompras - totalEstornos;
              const vendasPeriodo = Number(inventorySales || 0);
              const margemBruta = vendasPeriodo - comprasLiquidas;
              const margemPct = vendasPeriodo > 0 ? (margemBruta / vendasPeriodo) * 100 : 0;
              const maxBar = Math.max(comprasLiquidas, vendasPeriodo, 1);
              const compraW = `${Math.round((comprasLiquidas / maxBar) * 100)}%`;
              const vendaW = `${Math.round((vendasPeriodo / maxBar) * 100)}%`;
              return (
                <div className="bg-white rounded-md border border-slate-200 p-4 mb-4" data-testid="inventory-chart">
                  <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                    <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Compras × Vendas do Período</p>
                    <p className="text-xs text-slate-500">{movRange.start} → {movRange.end}</p>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="font-bold text-amber-700">Compras (Mercadoria)</span>
                        <span className="font-mono text-amber-700" data-testid="inv-chart-compras">R$ {comprasLiquidas.toFixed(2).replace('.', ',')}</span>
                      </div>
                      <div className="bg-slate-100 rounded h-6 overflow-hidden">
                        <div className="bg-amber-500 h-6 transition-all" style={{ width: compraW }} />
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="font-bold text-emerald-700">Vendas (Líquido)</span>
                        <span className="font-mono text-emerald-700" data-testid="inv-chart-vendas">R$ {vendasPeriodo.toFixed(2).replace('.', ',')}</span>
                      </div>
                      <div className="bg-slate-100 rounded h-6 overflow-hidden">
                        <div className="bg-emerald-500 h-6 transition-all" style={{ width: vendaW }} />
                      </div>
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t border-slate-100 flex flex-wrap items-center gap-3 justify-between">
                    <div className="text-sm">
                      <span className="text-slate-500 mr-2">Margem Bruta:</span>
                      <span className={`font-bold ${margemBruta >= 0 ? 'text-emerald-700' : 'text-red-600'}`} data-testid="inv-chart-margin">
                        R$ {margemBruta.toFixed(2).replace('.', ',')} {vendasPeriodo > 0 && `(${margemPct.toFixed(1)}%)`}
                      </span>
                    </div>
                    {vendasPeriodo === 0 && (
                      <p className="text-[10px] text-slate-400">Sem vendas registradas no período.</p>
                    )}
                  </div>
                </div>
              );
            })()}
            <div className="bg-white rounded-md border border-slate-200 overflow-hidden">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Data/Hora</th>
                    <th className="px-6 py-3 text-left text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Tipo</th>
                    <th className="px-6 py-3 text-left text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Categoria</th>
                    <th className="px-6 py-3 text-left text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Descrição</th>
                    <th className="px-6 py-3 text-left text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Valor</th>
                    <th className="px-6 py-3 text-left text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Usuário</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200" data-testid="inventory-table-body">
                  {inventoryEntries.length === 0 && (
                    <tr><td colSpan={6} className="text-center py-10 text-slate-400">Nenhuma entrada de mercadoria no período</td></tr>
                  )}
                  {inventoryEntries.map((entry) => (
                    <tr key={entry.id} className="hover:bg-slate-50" data-testid={`inventory-row-${entry.id}`}>
                      <td className="px-6 py-4 text-sm text-slate-600">{new Date(entry.created_at).toLocaleString('pt-BR')}</td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          {entry.type === 'entrada' ? <TrendingUp size={18} className="text-green-600" /> : <TrendingDown size={18} className="text-red-600" />}
                          <span className="capitalize">{entry.type}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4"><span className="px-2 py-1 rounded-md text-xs font-medium bg-emerald-100 text-emerald-700 capitalize">{entry.category}</span></td>
                      <td className="px-6 py-4 text-sm text-slate-900">{entry.description}</td>
                      <td className="px-6 py-4"><span className={`font-bold ${entry.type === 'entrada' ? 'text-green-600' : 'text-red-600'}`}>{entry.type === 'entrada' ? '+' : '-'} R$ {Number(entry.amount).toFixed(2)}</span></td>
                      <td className="px-6 py-4 text-sm text-slate-600">{entry.user_name || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="daily">
            <div className="flex items-center gap-2 mb-4 flex-wrap">
              <Label className="font-bold">Mês:</Label>
              <Select value={String(month)} onValueChange={(v) => setMonth(parseInt(v))}>
                <SelectTrigger className="w-32" data-testid="daily-month"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MONTHS_PT.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={String(year)} onValueChange={(v) => setYear(parseInt(v))}>
                <SelectTrigger className="w-28" data-testid="daily-year"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {years.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                </SelectContent>
              </Select>
              {daily && (
                <Button
                  onClick={handlePrintDaily}
                  variant="outline"
                  className="border-blue-500 text-blue-700 hover:bg-blue-50 ml-auto"
                  data-testid="daily-print"
                >
                  <Printer size={16} className="mr-2" /> Imprimir
                </Button>
              )}
            </div>

            <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-3" data-testid="daily-title">
              <CalendarIcon size={22} className="text-blue-600" />
              Movimentação Diária — {MONTHS_PT[month - 1]} / {year}
            </h2>

            {daily && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                  <div className="bg-white rounded-md border border-slate-200 p-4">
                    <p className="text-xs uppercase tracking-wider text-slate-500">Vendas (Bruto)</p>
                    <p className="text-2xl font-bold text-blue-600">R$ {daily.totals.sales_gross.toFixed(2)}</p>
                  </div>
                  <div className="bg-white rounded-md border border-slate-200 p-4">
                    <p className="text-xs uppercase tracking-wider text-slate-500">Custo de Vendas</p>
                    <p className="text-2xl font-bold text-yellow-700">R$ {daily.totals.cost_of_goods.toFixed(2)}</p>
                  </div>
                  <div className="bg-white rounded-md border border-slate-200 p-4">
                    <p className="text-xs uppercase tracking-wider text-slate-500">Lucro Estimado</p>
                    <p className="text-2xl font-bold text-green-600">R$ {daily.totals.profit.toFixed(2)}</p>
                  </div>
                  <div className="bg-white rounded-md border border-slate-200 p-4">
                    <p className="text-xs uppercase tracking-wider text-slate-500">Nº de Vendas</p>
                    <p className="text-2xl font-bold text-slate-900">{daily.totals.sales_count}</p>
                  </div>
                </div>

                <div className="bg-white rounded-md border border-slate-200 overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Dia</th>
                        <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-wider text-slate-500">Vendas (Qtd)</th>
                        <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-wider text-slate-500">Total Vendas</th>
                        <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-wider text-slate-500">Custo Vendas</th>
                        <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-wider text-slate-500">Lucro</th>
                        <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-wider text-slate-500">Saídas</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {daily.days.map((d) => (
                        <tr key={d.day} className={`hover:bg-slate-50 ${d.sales_gross > 0 ? '' : 'text-slate-400'}`}>
                          <td className="px-4 py-2 font-medium">{String(d.day).padStart(2, '0')}/{MONTHS_PT[month - 1].slice(0, 3)}/{year}</td>
                          <td className="px-4 py-2 text-right">{d.sales_count}</td>
                          <td className="px-4 py-2 text-right font-medium text-blue-600">R$ {d.sales_gross.toFixed(2)}</td>
                          <td className="px-4 py-2 text-right text-yellow-700">R$ {d.cost_of_goods.toFixed(2)}</td>
                          <td className={`px-4 py-2 text-right font-bold ${d.profit > 0 ? 'text-green-600' : 'text-slate-400'}`}>R$ {d.profit.toFixed(2)}</td>
                          <td className="px-4 py-2 text-right text-red-600">R$ {d.cash_out.toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-slate-100 font-bold">
                      <tr>
                        <td className="px-4 py-3">Total</td>
                        <td className="px-4 py-3 text-right">{daily.totals.sales_count}</td>
                        <td className="px-4 py-3 text-right text-blue-600">R$ {daily.totals.sales_gross.toFixed(2)}</td>
                        <td className="px-4 py-3 text-right text-yellow-700">R$ {daily.totals.cost_of_goods.toFixed(2)}</td>
                        <td className="px-4 py-3 text-right text-green-600">R$ {daily.totals.profit.toFixed(2)}</td>
                        <td className="px-4 py-3 text-right text-red-600">R$ {daily.totals.cash_out.toFixed(2)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </>
            )}
          </TabsContent>

          <TabsContent value="report">
            <DateRangeFilter
              value={{ start: reportStart, end: reportEnd }}
              onChange={({ start, end }) => { setReportStart(start); setReportEnd(end); }}
              className="mb-3"
            />
            <div className="bg-white rounded-md border border-slate-200 p-4 mb-4 flex flex-wrap gap-3 items-end">
              <Button onClick={loadReport} className="bg-blue-600 hover:bg-blue-700" data-testid="report-load">
                <FileBarChart size={16} className="mr-2" /> Gerar Relatório
              </Button>
              {report && (
                <Button
                  onClick={() => {
                    const rows = [];
                    // Linhas: vendas por forma
                    (report.payment_methods || []).forEach((p) => {
                      rows.push({ secao: 'Vendas por forma', desc: p.label || p.method, qtd: p.count || 0, valor: p.total || 0 });
                    });
                    rows.push({ secao: 'TOTAL VENDAS', desc: '', qtd: report.total_sales_count || 0, valor: report.total_sales || 0 });
                    (report.entradas || []).forEach((e) => rows.push({ secao: 'Entradas', desc: e.description, qtd: '', valor: e.amount }));
                    (report.saidas || []).forEach((e) => rows.push({ secao: 'Saídas', desc: e.description, qtd: '', valor: e.amount }));
                    rows.push({ secao: 'SALDO FINAL', desc: '', qtd: '', valor: (report.total_sales || 0) + (report.total_entradas || 0) - (report.total_saidas || 0) });
                    downloadCsv(`relatorio_caixa_${reportStart}_a_${reportEnd}.csv`, rows, [
                      { key: 'secao', label: 'Seção' },
                      { key: 'desc', label: 'Descrição' },
                      { key: 'qtd', label: 'Qtd' },
                      { key: 'valor', label: 'Valor (R$)', format: (v) => v === '' ? '' : Number(v || 0).toFixed(2).replace('.', ',') },
                    ]);
                  }}
                  variant="outline"
                  className="border-emerald-500 text-emerald-700 hover:bg-emerald-50 print:hidden"
                  data-testid="report-export-csv"
                >
                  <FileBarChart size={16} className="mr-2" /> Exportar CSV
                </Button>
              )}
              {report && (
                <Button
                  onClick={handlePrintReport}
                  variant="outline"
                  className="border-blue-500 text-blue-700 hover:bg-blue-50 print:hidden"
                  data-testid="report-print"
                >
                  <Printer size={16} className="mr-2" /> Imprimir Relatório
                </Button>
              )}
            </div>

            {report && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                  <div className="bg-white rounded-md border border-slate-200 p-4">
                    <p className="text-xs uppercase tracking-wider text-slate-500">Vendas</p>
                    <p className="text-3xl font-black text-slate-900">{report.sales_count}</p>
                  </div>
                  <div className="bg-white rounded-md border border-slate-200 p-4">
                    <p className="text-xs uppercase tracking-wider text-slate-500">Total Bruto</p>
                    <p className="text-2xl font-bold text-slate-700">R$ {report.total_bruto.toFixed(2)}</p>
                  </div>
                  <div className="bg-white rounded-md border border-slate-200 p-4">
                    <p className="text-xs uppercase tracking-wider text-red-700">Descontos</p>
                    <p className="text-2xl font-bold text-red-600">- R$ {report.total_desconto.toFixed(2)}</p>
                  </div>
                  <div className="bg-blue-600 rounded-md p-4 text-white">
                    <p className="text-xs uppercase tracking-wider opacity-90">Total Líquido</p>
                    <p className="text-3xl font-black">R$ {report.total_liquido.toFixed(2)}</p>
                    <p className="text-xs opacity-80">Ticket médio: R$ {report.ticket_medio.toFixed(2)}</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Vendas por Forma de Pagamento */}
                  <div className="bg-white rounded-md border border-slate-200 overflow-hidden">
                    <div className="bg-slate-50 px-4 py-3 border-b border-slate-200">
                      <h3 className="font-bold text-slate-900">Vendas por Forma de Pagamento</h3>
                    </div>
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs uppercase tracking-wider text-slate-500 font-bold">Forma</th>
                          <th className="px-4 py-2 text-right text-xs uppercase tracking-wider text-slate-500 font-bold">Vendas</th>
                          <th className="px-4 py-2 text-right text-xs uppercase tracking-wider text-slate-500 font-bold">Valor</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200">
                        {report.by_payment_method.length === 0 && <tr><td colSpan={3} className="text-center py-4 text-slate-400">Sem dados</td></tr>}
                        {report.by_payment_method.map(p => (
                          <tr key={p.method}>
                            <td className="px-4 py-2 capitalize">{p.method.replace('_', ' ')}</td>
                            <td className="px-4 py-2 text-right">{p.count}</td>
                            <td className="px-4 py-2 text-right font-bold text-blue-600">R$ {p.total.toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Vendas por Categoria */}
                  <div className="bg-white rounded-md border border-slate-200 overflow-hidden">
                    <div className="bg-slate-50 px-4 py-3 border-b border-slate-200">
                      <h3 className="font-bold text-slate-900">Vendas por Categoria de Produto</h3>
                    </div>
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs uppercase tracking-wider text-slate-500 font-bold">Categoria</th>
                          <th className="px-4 py-2 text-right text-xs uppercase tracking-wider text-slate-500 font-bold">Quantidade</th>
                          <th className="px-4 py-2 text-right text-xs uppercase tracking-wider text-slate-500 font-bold">Valor</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200">
                        {report.by_category.length === 0 && <tr><td colSpan={3} className="text-center py-4 text-slate-400">Sem dados</td></tr>}
                        {report.by_category.map(c => (
                          <tr key={c.category}>
                            <td className="px-4 py-2">
                              <span className="inline-block w-2 h-2 rounded-full mr-2" style={{ background: CATEGORY_COLORS[c.category] || '#64748b' }}></span>
                              {CATEGORY_LABELS[c.category] || c.category}
                            </td>
                            <td className="px-4 py-2 text-right">{c.quantity}</td>
                            <td className="px-4 py-2 text-right font-bold text-blue-600">R$ {c.total.toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </TabsContent>

          <TabsContent value="cancelled">
            <DateRangeFilter
              value={cancelRange}
              onChange={setCancelRange}
              onExportCsv={() => {
                downloadCsv(`cancelamentos_${cancelRange.start}_a_${cancelRange.end}.csv`, cancelledSales, [
                  { key: 'cancelled_at', label: 'Cancelada em', format: (v) => v ? new Date(v).toLocaleString('pt-BR') : '' },
                  { key: 'type', label: 'Tipo', format: (v) => v === 'mesa' ? 'Mesa' : v === 'pre_sale' ? 'Pré-venda' : v === 'cart_item' ? 'Item' : 'Venda' },
                  { key: 'label', label: 'Identificação' },
                  { key: 'customer_name', label: 'Cliente' },
                  { key: (r) => r.cancelled_by_name || r.cashier_name, label: 'Cancelado por' },
                  { key: 'total', label: 'Valor (R$)', format: (v) => Number(v || 0).toFixed(2).replace('.', ',') },
                  { key: 'cancelled_reason', label: 'Motivo' },
                ]);
              }}
              exportLabel="Exportar CSV"
              className="mb-3"
            />
            <div className="bg-white rounded-md border border-slate-200 overflow-hidden">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Cancelada em</th>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Tipo</th>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Identificação</th>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Cliente</th>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Cancelado por</th>
                    <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-wider text-slate-500">Valor</th>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Motivo</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {cancelledSales.length === 0 && (
                    <tr><td colSpan={7} className="text-center py-10 text-slate-400">Nenhum cancelamento registrado</td></tr>
                  )}
                  {cancelledSales.map((s) => {
                    const typeBadge =
                      s.type === 'mesa' ? { txt: 'Mesa', cls: 'bg-amber-100 text-amber-800' }
                      : s.type === 'pre_sale' ? { txt: 'Pré-venda', cls: 'bg-yellow-100 text-yellow-800' }
                      : s.type === 'cart_item' ? { txt: 'Item', cls: 'bg-purple-100 text-purple-800' }
                      : { txt: 'Venda', cls: 'bg-red-100 text-red-800' };
                    return (
                      <tr key={s.id} className="hover:bg-red-50" data-testid="cancellation-row">
                        <td className="px-4 py-3 text-sm">{s.cancelled_at ? new Date(s.cancelled_at).toLocaleString('pt-BR') : '-'}</td>
                        <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${typeBadge.cls}`}>{typeBadge.txt}</span></td>
                        <td className="px-4 py-3 font-bold">{s.label || (s.sale_number ? `#${s.sale_number}` : '-')}</td>
                        <td className="px-4 py-3 text-sm">{s.customer_name || '-'}</td>
                        <td className="px-4 py-3 text-sm">{s.cancelled_by_name || s.cashier_name || '-'}</td>
                        <td className="px-4 py-3 text-right font-bold text-red-600 line-through">R$ {Number(s.total).toFixed(2)}</td>
                        <td className="px-4 py-3 text-sm text-slate-600">{s.cancelled_reason || '-'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="crediario">
            <div className="bg-white rounded-md border border-slate-200 p-4" data-testid="crediario-section">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-12 h-12 rounded-md bg-emerald-600 flex items-center justify-center">
                  <Wallet className="text-white" size={24} />
                </div>
                <div className="flex-1">
                  <h2 className="text-2xl font-bold text-slate-900">Crediário & Recebimentos</h2>
                  <p className="text-sm text-slate-600">Receba contas a prazo e veja relatórios completos</p>
                </div>
                <Button onClick={() => setShowCrediarioModal(true)} className="bg-emerald-600 hover:bg-emerald-700 h-10" data-testid="open-crediario-receive">
                  <Wallet size={16} className="mr-2" /> Buscar cliente e receber
                </Button>
              </div>

              <InnerTabs defaultValue="report" className="mt-2">
                <InnerTabsList className="grid w-full md:w-[400px] grid-cols-2 mb-4">
                  <InnerTabsTrigger value="report" data-testid="crediario-tab-report">Relatório</InnerTabsTrigger>
                  <InnerTabsTrigger value="info" data-testid="crediario-tab-info">Atalhos</InnerTabsTrigger>
                </InnerTabsList>
                <InnerTabsContent value="report">
                  <ReceivablesReport />
                </InnerTabsContent>
                <InnerTabsContent value="info">
                  <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-sm text-blue-900">
                    <p className="mb-2"><strong>Onde mais posso receber?</strong></p>
                    <ul className="list-disc pl-5 space-y-1">
                      <li>No <strong>PDV</strong> com a tecla <kbd className="px-1.5 py-0.5 bg-blue-100 rounded font-mono">F7</kbd> ou <kbd className="px-1.5 py-0.5 bg-blue-100 rounded font-mono">R</kbd> (mesmo com cursor no campo barcode)</li>
                      <li>Aqui em <strong>Fluxo de Caixa → Crediário</strong> (este atalho)</li>
                      <li>Cada recebimento entra automaticamente no <strong>caixa aberto</strong> do operador atual e aparece em Movimentações</li>
                      <li>Vendas a prazo vencem em <strong>30 dias</strong> · após isso, juros de <strong>2% a.m.</strong> são aplicados automaticamente</li>
                    </ul>
                  </div>
                </InnerTabsContent>
              </InnerTabs>
            </div>
          </TabsContent>

          <TabsContent value="cash-session">
            <CashSessionContent />
          </TabsContent>

          <TabsContent value="shifts">
            <ShiftSummaryContent />
          </TabsContent>
        </Tabs>
        <PDVReceivablesModal open={showCrediarioModal} onClose={() => setShowCrediarioModal(false)} onReceived={() => {}} />
      </div>
      {profitCat && (
        <CategoryProfitModal
          category={profitCat}
          label={CATEGORY_LABELS[profitCat] || profitCat}
          color={CATEGORY_COLORS[profitCat] || '#64748b'}
          range={movRange}
          onClose={() => setProfitCat(null)}
        />
      )}
    </Layout>
  );
};
